import React, { useMemo, useRef } from 'react';
import { Printer } from 'lucide-react';

import { buildComprobanteHtml } from '../utils/printComprobante';

/**
 * El comprobante, a la vista dentro de la pantalla.
 *
 * Antes la factura se abría en una ventana aparte, y para verla había un clic
 * de por medio. Dos problemas con eso: el cajero no ve lo que acaba de emitir
 * hasta que lo pide, y una ventana emergente puede quedar bloqueada por el
 * navegador —el botón parece no andar y no hay forma de saber por qué—.
 *
 * Acá el documento se dibuja apenas se registra la venta. Imprimir es un botón
 * más, no un paso aparte.
 *
 * Va en un `iframe` y no en un `div` a propósito: el documento trae su propia
 * hoja de estilos, con medidas de papel y tipografía monoespaciada. Dentro de
 * la página se mezclaría con el resto —y al imprimir saldría la pantalla
 * entera en vez del comprobante—. El `iframe` lo aísla y permite imprimir
 * exactamente lo que se ve.
 */
export default function VistaComprobante({ venta, documento, settings, alto = 440 }) {
    const marco = useRef(null);

    // Se arma una sola vez por venta: volver a generarlo en cada render haría
    // parpadear el documento.
    const html = useMemo(
        () => buildComprobanteHtml(venta, documento, settings, { conBarra: false }),
        [venta, documento, settings],
    );

    const imprimir = () => {
        const ventana = marco.current?.contentWindow;
        if (!ventana) return;
        // Enfocar antes de imprimir: sin esto, algunos navegadores imprimen la
        // página que contiene al marco en lugar del marco.
        ventana.focus();
        ventana.print();
    };

    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white overflow-hidden">
                <iframe
                    ref={marco}
                    title="Comprobante"
                    srcDoc={html}
                    className="w-full block"
                    style={{ height: alto, border: 0 }}
                />
            </div>
            <button
                type="button"
                onClick={imprimir}
                className="boton-principal w-full flex items-center justify-center gap-2"
            >
                <Printer size={18} />
                Imprimir
            </button>
        </div>
    );
}
